import {useEffect, createContext} from 'react';

import * as store from '@/store';

/**
 * 根据页面宽度，实时计算header中各部件占据的宽度，来决定收缩或隐藏哪些部件
 * 同时渲染了两个haeder，其中一个不可见，用于计算各部件的宽度
 */

export interface IAdapterData {
    [key: string]: boolean;
}

export const AdapterDataContext = createContext<IAdapterData>({});

export const placeholderElMinWidth = 84;

// 事件名
export const shrinkDocTitle = 'shrinkDocTitle';
export const minimizeCollaboratorList = 'minimizeCollaboratorList';
export const foldBtns = 'foldBtns';
export const shrinkNavDropdown = 'shrinkNavDropdown';
export const hideNavDropdown = 'hideNavDropdown';
export const hideDocTitle = 'hideDocTitle';
export const hidePublicTag = 'hidePublicTag';
export const hidePublishTag = 'hidePublishTag';
export const hideUpdateTime = 'hideUpdateTime';
export const hideSaveStatus = 'hideSaveStatus';
export const hideCollaboratorList = 'hideCollaboratorList';
export const hideDropMenuBtn = 'hideDropMenuBtn';
export const hideEditAndUpdateBtn = 'hideEditAndUpdateBtn';
export const hideCreateBtn = 'hideCreateBtn';
export const shrinkPlaceholderEl = 'shrinkPlaceholderEl';
export const hideLogoTitle = 'hideLogoTitle';
export const hideBtnsText = 'hideBtnsText';

// 数组顺序代表事件触发的优先级
const eventList = [
    hideBtnsText,
    shrinkDocTitle,
    minimizeCollaboratorList,
    foldBtns,
    shrinkNavDropdown,
    hideNavDropdown,
    hideDocTitle,
    hidePublicTag,
    hidePublishTag,
    hideUpdateTime,
    hideSaveStatus,
];

// 此部分目前在搜索展开时做隐藏
const partEventList = [
    hideCollaboratorList,
    hideDropMenuBtn,
    hideEditAndUpdateBtn,
    hideLogoTitle,
    shrinkPlaceholderEl,
];

function getNodesWidth(nodes: NodeListOf<HTMLElement>) {
    let width = 0;
    nodes.forEach(node => {
        const style = window.getComputedStyle(node);
        const marginLeft = parseInt(style.marginLeft, 10);
        const marginRight = parseInt(style.marginRight, 10);
        width += node.offsetWidth + marginLeft + marginRight;
    });

    return width;
}

// 最小化时占据的空间 width + margin
const minSpaceDocTitle = 58;
const minSpaceNavDropdown = 104;
const minSpaceCollaboratorList = 83;

// 方法列表，返回该事件执行后能空出的宽度
const eventWidthFnMap: any = {
    [shrinkDocTitle]: (nodes: NodeListOf<HTMLElement>) => {
        const val = getNodesWidth(nodes) - minSpaceDocTitle;
        return val > 0 ? val : 0;
    },
    [minimizeCollaboratorList]: (nodes: NodeListOf<HTMLElement>) => {
        // 最小占据空间 width + margin
        const val = getNodesWidth(nodes) - minSpaceCollaboratorList;
        return val;
    },
    [shrinkNavDropdown]: (nodes: NodeListOf<HTMLElement>) => {
        const val = getNodesWidth(nodes) - minSpaceNavDropdown;
        return val > 0 ? val : 0;
    },
    [hideNavDropdown]: () => {
        return minSpaceNavDropdown;
    },
    [hideDocTitle]: () => {
        return minSpaceDocTitle;
    },
    [hideCollaboratorList]: () => {
        return minSpaceCollaboratorList;
    },
    [shrinkPlaceholderEl]: (nodes: NodeListOf<HTMLElement>) => {
        return placeholderElMinWidth - getNodesWidth(nodes);
    },
};

type IDivRef = React.MutableRefObject<HTMLDivElement | null>;
let wrapElRef: IDivRef = {current: null};
let headerElRef: IDivRef = {current: null};
// 某些场景下额外产生的宽度，比如搜索展开时
let extraSpace: number = 0;
let isRepositoryPage: boolean = false;

const getEventList = () => {
    const list = extraSpace ? [...eventList, ...partEventList] : eventList;

    if (extraSpace && isRepositoryPage) {
        list.push(hideCreateBtn);
    }

    return list;
};

const update = () => {
    const wrapEl = wrapElRef.current;
    const headerEl = headerElRef.current;
    if (!headerEl || !wrapEl) {
        return;
    }

    const wrapWidth = wrapEl.offsetWidth;
    const headerWdith = headerEl.offsetWidth;

    let gap = headerWdith + extraSpace - wrapWidth;
    const data: IAdapterData = {};
    const event = getEventList();

    if (gap > 0) {
        event.some(name => {
            const nodes: NodeListOf<HTMLElement> = headerEl.querySelectorAll(
                `[data-adapter-event-name~="${name}"]`
            );
            const width = (eventWidthFnMap[name] || getNodesWidth)(nodes);
            gap = gap - width;
            data[name] = true;

            if (gap <= 0) {
                return true;
            }
            return false;
        });
    }

    store.setHeaderAdapterData(data);
};

export const useAdapter = (wrapRef: IDivRef, headerRef: IDivRef) => {
    wrapElRef = wrapRef;
    headerElRef = headerRef;

    useEffect(() => {
        window.addEventListener('resize', update);

        // header总宽度变化时更新
        let ro: ResizeObserver | null = null;
        if (window.ResizeObserver) {
            ro = new window.ResizeObserver(entries => {
                if (entries.length) {
                    update();
                }
            });
            headerElRef.current && ro.observe(headerElRef.current);
        }

        return () => {
            window.removeEventListener('resize', update);
            ro && ro.disconnect();
        };
    }, []);
};

export const updateHeaderAdapter = (space: number = 0) => {
    extraSpace = space;
    update();
};

export const setIsRepositoryPage = (val: boolean) => {
    isRepositoryPage = val;
};
